// This file is automatically compiled by Webpack, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/javascript and only use these pack files to reference
// that code so it'll be compiled.
//
// To reference this file, add <%= javascript_pack_tag 'application' %> to the appropriate
// layout file, like app/views/layouts/application.html.erb

import 'channels';
import { Application, defaultSchema } from '@hotwired/stimulus';
import { definitionsFromContext } from '@hotwired/stimulus-webpack-helpers';
import { logTurboEvents } from '../utils/turbo_events';

// Stimulus reads the event.key
const customSchema = {
  ...defaultSchema,
  keyMappings: {
    ...defaultSchema.keyMappings,
    delete: 'Delete',
    numpadMinus: '-',
  },
};

const application = Application.start(document.documentElement, customSchema);

// #AI: Load controllers only after ensuring globals are available
// This prevents mobile browser issues where Sprockets loads after Webpack
const loadControllersWhenReady = () => {
  const context = require.context('controllers', true, /.(js|ts)$/);
  const context_components = require.context(
    '../../components',
    true,
    /_controller.(js|ts)$/
  );

  application.load(
    definitionsFromContext(context).concat(
      definitionsFromContext(context_components)
    )
  );
};

// Check if globals are ready, otherwise wait.
// #AI: previously this waited a single fixed 100ms after DOMContentLoaded and
// then called loadControllersWhenReady() unconditionally, WITHOUT re-checking
// that jQuery/EB had actually finished loading. On slower networks/devices
// (or a slow-compiling dev server) the Sprockets bundle can still be loading
// past that single 100ms window, so Stimulus controllers (e.g. select2-based
// ones) would initialize before `$`/`jQuery` existed, producing "jQuery is
// missing"-class errors at the controller's call site. Poll until the
// globals are actually ready (bounded, so a genuinely broken asset load still
// fails loudly instead of hanging forever).
const areGlobalsReady = () => typeof window.jQuery !== 'undefined' && typeof window.EB !== 'undefined';

const MAX_GLOBALS_WAIT_ATTEMPTS = 50; // 50 * 100ms = 5s

const waitForGlobalsThenLoadControllers = (attempt = 0) => {
  if (areGlobalsReady()) {
    loadControllersWhenReady();
    return;
  }

  if (attempt >= MAX_GLOBALS_WAIT_ATTEMPTS) {
    console.error('[Stimulus] jQuery/EB globals never became available after 5s; loading controllers anyway.');
    loadControllersWhenReady();
    return;
  }

  setTimeout(() => waitForGlobalsThenLoadControllers(attempt + 1), 100);
};

if (areGlobalsReady()) {
  loadControllersWhenReady();
} else {
  // Wait for DOMContentLoaded, then keep polling until globals are ready.
  document.addEventListener('DOMContentLoaded', () => {
    waitForGlobalsThenLoadControllers();
  });
}

application.debug = process.env.RAILS_ENV === 'development';

// Pick up the client timezone for localtime conversions on the site.
// We use local_time gem throughout for easy localization for logged in users,
// but we still need to determine and save (server-side) the tz for users
// so we have a default timezone to show to voters.
import Cookies from 'js-cookie';
import jstz from 'jstz';
// `secure` cookies are only stored over HTTPS *and* in a secure context.
// Keying off the actual page protocol keeps prod (HTTPS) secure while letting
// the cookie persist in dev served over plain HTTP (incl. bare-IP/LAN access),
// where `secure: true` would be silently dropped by the browser.
Cookies.set('timezone', jstz.determine().name(), {
  path: '/',
  secure: window.location.protocol === 'https:',
  expires: 365,
  SameSite: 'strict',
});

import '../components';
import I18n from 'i18n-js/index.js.erb';
window.I18n = I18n;

import '@hotwired/turbo-rails';

// Opt-in approach, our legacy code is not fully compatible with Turbo
Turbo.session.drive = false;

// Debug Turbo Stream connections
document.addEventListener('turbo:before-stream-render', (event) => {
  console.log('[Turbo] Stream render:', event.detail);
});

// Debug ActionCable connection for Turbo Streams
if (typeof Turbo.StreamActions !== 'undefined') {
  console.log('[Turbo] StreamActions available');

  // Watch for turbo-cable-stream-source elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.tagName === 'TURBO-CABLE-STREAM-SOURCE') {
          console.log('[Turbo] Stream source added:', node);
          console.log('[Turbo] Channel:', node.getAttribute('channel'));
          console.log('[Turbo] Signed stream name:', node.getAttribute('signed-stream-name'));
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}

import 'chartkick/chart.js';

// Import chart patterns helper for applying patterns to Chartkick charts
import '../lib/chart_patterns.js';

import '../pagy/pagy.js.erb';

import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import Quill from 'quill';
window.Quill = Quill;

// Register custom blots once globally so neither the Stimulus controller
// nor the legacy CoffeeScript need to re-register them.
const BlockEmbed = Quill.import('blots/block/embed');
class DividerBlot extends BlockEmbed {}
DividerBlot.blotName = 'divider';
DividerBlot.tagName = 'hr';
Quill.register(DividerBlot);

const Embed = Quill.import('blots/embed');
class BreakBlot extends Embed {
  static blotName = 'break';
  static tagName = 'br';
  static value() { return true; }
  length() { return 1; }
  value() { return true; }
}
Quill.register(BreakBlot);

logTurboEvents();
